import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { ConfirmDialog } from './confirm-dialog';

export interface ConfirmOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red confirm button + warning icon — use for destructive actions. */
  danger?: boolean;
}

export interface PromptOptions {
  title: string;
  message?: string;
  defaultValue?: string;
  placeholder?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  inputType?: 'text' | 'url' | 'email' | 'number';
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;
type PromptFn = (opts: PromptOptions) => Promise<string | null>;

const ConfirmContext = createContext<ConfirmFn | null>(null);
const PromptContext = createContext<PromptFn | null>(null);

/**
 * App-wide branded dialogs. Mount <ConfirmProvider> once near the root, then:
 *   const confirm = useConfirm();  // if (await confirm({ title, message, danger })) {}
 *   const prompt  = usePrompt();   // const v = await prompt({ title, defaultValue }); if (v === null) return;
 * On-brand replacements for the native window.confirm() / window.prompt().
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  // ── confirm ──
  const [confirmOpts, setConfirmOpts] = useState<ConfirmOptions | null>(null);
  const confirmResolver = useRef<((v: boolean) => void) | null>(null);
  const confirm = useCallback<ConfirmFn>((options) => {
    setConfirmOpts(options);
    return new Promise<boolean>((resolve) => {
      confirmResolver.current = resolve;
    });
  }, []);
  const settleConfirm = useCallback((result: boolean) => {
    const resolve = confirmResolver.current;
    confirmResolver.current = null;
    setConfirmOpts(null);
    resolve?.(result);
  }, []);

  // ── prompt ──
  const [promptOpts, setPromptOpts] = useState<PromptOptions | null>(null);
  const promptResolver = useRef<((v: string | null) => void) | null>(null);
  const prompt = useCallback<PromptFn>((options) => {
    setPromptOpts(options);
    return new Promise<string | null>((resolve) => {
      promptResolver.current = resolve;
    });
  }, []);
  const settlePrompt = useCallback((result: string | null) => {
    const resolve = promptResolver.current;
    promptResolver.current = null;
    setPromptOpts(null);
    resolve?.(result);
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      <PromptContext.Provider value={prompt}>
        {children}
        <ConfirmDialog
          open={Boolean(confirmOpts)}
          title={confirmOpts?.title ?? ''}
          message={confirmOpts?.message ?? ''}
          confirmLabel={confirmOpts?.confirmLabel}
          cancelLabel={confirmOpts?.cancelLabel}
          danger={confirmOpts?.danger}
          onConfirm={() => settleConfirm(true)}
          onCancel={() => settleConfirm(false)}
        />
        <PromptDialog
          opts={promptOpts}
          onSubmit={(v) => settlePrompt(v)}
          onCancel={() => settlePrompt(null)}
        />
      </PromptContext.Provider>
    </ConfirmContext.Provider>
  );
}

/** Imperative confirm: `const confirm = useConfirm(); if (await confirm({...})) {}` */
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within <ConfirmProvider>');
  return ctx;
}

/** Imperative prompt: `const prompt = usePrompt(); const v = await prompt({...});` (null = cancelled) */
export function usePrompt(): PromptFn {
  const ctx = useContext(PromptContext);
  if (!ctx) throw new Error('usePrompt must be used within <ConfirmProvider>');
  return ctx;
}

// ---------------------------------------------------------------------------
// Branded prompt dialog (single-line input) — same overlay style as ConfirmDialog.
// ---------------------------------------------------------------------------
function PromptDialog({
  opts,
  onSubmit,
  onCancel,
}: {
  opts: PromptOptions | null;
  onSubmit: (value: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset to the default value each time the dialog opens, then focus.
  useEffect(() => {
    if (opts) {
      setValue(opts.defaultValue ?? '');
      // focus after paint
      const id = setTimeout(() => inputRef.current?.select(), 30);
      return () => clearTimeout(id);
    }
  }, [opts]);

  if (!opts || typeof document === 'undefined') return null;

  const submit = () => {
    if (!value.trim()) return;
    onSubmit(value.trim());
  };

  return createPortal(
    <>
      <div className="fixed inset-0 z-[10000] bg-black/50" onClick={onCancel} />
      <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4">
        <div
          className="bg-card w-full max-w-sm rounded-xl border shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <form
            className="p-6"
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            <h2 className="text-lg font-semibold">{opts.title}</h2>
            {opts.message && <p className="text-muted-foreground mt-1 text-sm">{opts.message}</p>}
            <input
              ref={inputRef}
              type={opts.inputType ?? 'text'}
              value={value}
              placeholder={opts.placeholder}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') onCancel();
              }}
              className="border-input bg-background focus:ring-primary/40 mt-4 w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2"
            />
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={onCancel}
                className="hover:bg-muted flex-1 rounded-lg border px-4 py-2 text-sm font-medium transition-colors"
              >
                {opts.cancelLabel ?? 'Cancel'}
              </button>
              <button
                type="submit"
                disabled={!value.trim()}
                className="bg-primary hover:bg-primary/90 flex-1 rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {opts.confirmLabel ?? 'OK'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>,
    document.body
  );
}
